import { useCallback, useEffect, useState } from 'react';
import {
  createPracticeMediator,
  createPracticeSource,
  getPractice,
  patchPracticeBrand,
  patchPracticeMediator,
  patchPracticeSource,
  EngineError,
  type PracticePayload,
  type PracticeSource,
} from '../../api/engineDashboard';

interface Props {
  token: string;
  onUnauthorized: () => void;
}

/**
 * Practice self-service (white-label tenants, e.g. Mediate Colorado):
 * brand, sources (public intake links + logos), and the practice's own
 * mediator accounts. Everything here is scoped server-side to the signed-in
 * admin's practice — this page simply renders what /practice returns.
 */
export function PracticeSettingsPage({ token, onUnauthorized }: Props) {
  const [data, setData] = useState<PracticePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getPractice(token)
      .then(setData)
      .catch((e) => {
        if (e instanceof EngineError && e.status === 401) return onUnauthorized();
        setError(e instanceof Error ? e.message : 'Failed to load practice');
      });
  }, [token, onUnauthorized]);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<void>, successNote?: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      if (successNote) setNotice(successNote);
      load();
    } catch (e) {
      if (e instanceof EngineError && e.status === 401) return onUnauthorized();
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return <div className="mx-auto max-w-4xl p-6 text-sm text-gray-500">{error ?? 'Loading your practice…'}</div>;
  }
  const brand = data.practice.brand_config as Record<string, string | undefined>;

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4">
      <header>
        <h1 className="text-xl font-bold text-gray-900">{data.practice.name}</h1>
        <p className="text-sm text-gray-500">
          Your practice runs on Levelheaded rails — everything below is yours to manage.
        </p>
      </header>
      {error && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
      {notice && (
        <div className="flex items-start justify-between rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          <span className="whitespace-pre-wrap">{notice}</span>
          <button type="button" className="ml-3 text-xs font-medium underline" onClick={() => setNotice(null)}>dismiss</button>
        </div>
      )}

      <BrandCard brand={brand} busy={busy} onSave={(b) => run(async () => { await patchPracticeBrand(token, b); }, 'Brand settings saved.')} />
      <SourcesCard sources={data.sources} busy={busy}
        onCreate={(b) => run(async () => {
          const res = await createPracticeSource(token, b);
          setNotice(`Source created. Public intake link: https://resolve.belevelheaded.com${res.intake_path}`);
        })}
        onSave={(id, b) => run(async () => { await patchPracticeSource(token, id, b); }, 'Source updated.')}
      />
      <MediatorsCard mediators={data.mediators} busy={busy}
        onCreate={(b) => run(async () => {
          const res = await createPracticeMediator(token, b);
          setNotice(`${res.mediator.display_name} added.\nTemporary password (share it with them now — it is not shown again):\n${res.temp_password}`);
        })}
        onToggle={(id, active) => run(async () => { await patchPracticeMediator(token, id, { is_active: active }); })}
      />
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text' }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-gray-600">{label}</span>
      <input type={type} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-surface-border px-2.5 py-1.5 text-sm" />
    </label>
  );
}

function BrandCard({ brand, busy, onSave }: {
  brand: Record<string, string | undefined>; busy: boolean; onSave: (b: Record<string, string>) => void;
}) {
  const [name, setName] = useState(brand.brand_name ?? '');
  const [logo, setLogo] = useState(brand.logo_url ?? '');
  const [site, setSite] = useState(brand.site ?? '');
  const [email, setEmail] = useState(brand.support_email ?? '');
  const [phone, setPhone] = useState(brand.support_phone ?? '');
  return (
    <section className="card space-y-3 p-4">
      <div>
        <h2 className="font-semibold text-gray-900">Brand</h2>
        <p className="text-xs text-gray-500">
          Party-facing messages and intake pages speak as this brand. Emails send as
          “{(name || 'Your brand')} (via Levelheaded)” until your email domain is verified.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Brand name" value={name} onChange={setName} placeholder="Mediate Colorado" />
        <Field label="Website" value={site} onChange={setSite} placeholder="mediatecolorado.org" />
        <Field label="Logo URL (shown on intake forms + emails)" value={logo} onChange={setLogo} placeholder="https://…/logo.png" />
        <Field label="Support email" value={email} onChange={setEmail} type="email" />
        <Field label="Support phone" value={phone} onChange={setPhone} />
      </div>
      {logo && <img src={logo} alt="logo preview" className="max-h-16 rounded border border-surface-border bg-white p-1" />}
      <button type="button" className="btn-primary" disabled={busy || !name.trim()}
        onClick={() => onSave({ brand_name: name.trim(), ...(logo.trim() ? { logo_url: logo.trim() } : {}), ...(site.trim() ? { site: site.trim() } : {}), ...(email.trim() ? { support_email: email.trim() } : {}), ...(phone.trim() ? { support_phone: phone.trim() } : {}) })}>
        Save brand
      </button>
    </section>
  );
}

function SourcesCard({ sources, busy, onCreate, onSave }: {
  sources: PracticeSource[]; busy: boolean;
  onCreate: (b: { name: string; slug: string; logo_url?: string; support_email?: string }) => void;
  onSave: (id: string, b: Record<string, string>) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [logo, setLogo] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editLogo, setEditLogo] = useState('');
  return (
    <section className="card space-y-3 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-gray-900">Sources</h2>
          <p className="text-xs text-gray-500">Each source gets a public intake link and its own logo. Your whole team sees every source&apos;s cases.</p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => setAdding((v) => !v)}>{adding ? 'Cancel' : 'Add source'}</button>
      </div>
      {adding && (
        <div className="space-y-2 rounded-md border border-surface-border p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name" value={name} onChange={setName} placeholder="City of Arvada Mediation" />
            <Field label="Intake link path" value={slug} onChange={(v) => setSlug(v.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} placeholder="arvada" />
            <Field label="Logo URL (optional)" value={logo} onChange={setLogo} placeholder="https://…/logo.png" />
          </div>
          {slug && <p className="text-xs text-gray-500">Public link: resolve.belevelheaded.com/intake/<b>{slug}</b></p>}
          <button type="button" className="btn-primary" disabled={busy || !name.trim() || !slug.trim()}
            onClick={() => { onCreate({ name: name.trim(), slug: slug.trim(), ...(logo.trim() ? { logo_url: logo.trim() } : {}) }); setAdding(false); setName(''); setSlug(''); setLogo(''); }}>
            Create source
          </button>
        </div>
      )}
      <ul className="divide-y divide-surface-border">
        {sources.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center gap-3 py-2.5 text-sm">
            {s.logo_url ? <img src={s.logo_url} alt="" className="h-8 w-8 rounded object-contain" /> : <span className="flex h-8 w-8 items-center justify-center rounded bg-surface-muted text-xs text-gray-400">—</span>}
            <div className="min-w-0">
              <div className="font-medium text-gray-900">{s.name}</div>
              <div className="text-xs text-gray-500">
                {s.slug ? <>intake: /intake/{s.slug}</> : 'no intake link yet'}
                {s.sms_from_number ? ` · texts from ${s.sms_from_number}` : ''}
              </div>
            </div>
            <span className="ml-auto" />
            {editing === s.id ? (
              <span className="flex items-center gap-2">
                <input value={editLogo} placeholder="Logo URL" onChange={(e) => setEditLogo(e.target.value)}
                  className="w-56 rounded-md border border-surface-border px-2 py-1 text-xs" />
                <button type="button" className="btn-primary !py-1 !text-xs" disabled={busy}
                  onClick={() => { onSave(s.id, { logo_url: editLogo.trim() }); setEditing(null); }}>Save</button>
                <button type="button" className="btn-secondary !py-1 !text-xs" onClick={() => setEditing(null)}>Cancel</button>
              </span>
            ) : (
              <button type="button" className="btn-secondary !py-1 !text-xs" onClick={() => { setEditing(s.id); setEditLogo(s.logo_url ?? ''); }}>
                {s.logo_url ? 'Change logo' : 'Add logo'}
              </button>
            )}
          </li>
        ))}
        {sources.length === 0 && <li className="py-3 text-sm text-gray-500">No sources yet — add your first intake funnel above.</li>}
      </ul>
    </section>
  );
}

function MediatorsCard({ mediators, busy, onCreate, onToggle }: {
  mediators: PracticePayload['mediators']; busy: boolean;
  onCreate: (b: { email: string; display_name: string; role?: 'mediator' | 'admin' | 'admin_mediator' }) => void;
  onToggle: (id: string, active: boolean) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'mediator' | 'admin' | 'admin_mediator'>('mediator');
  return (
    <section className="card space-y-3 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-gray-900">Your team</h2>
          <p className="text-xs text-gray-500">Mediators handle cases; admins manage this page; "Admin + mediator" does both. New accounts get a one-time temporary password.</p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => setAdding((v) => !v)}>{adding ? 'Cancel' : 'Add person'}</button>
      </div>
      {adding && (
        <div className="space-y-2 rounded-md border border-surface-border p-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Full name" value={name} onChange={setName} />
            <Field label="Email" value={email} onChange={setEmail} type="email" />
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-gray-600">Role</span>
              <select value={role} onChange={(e) => setRole(e.target.value as 'mediator' | 'admin' | 'admin_mediator')}
                className="w-full rounded-md border border-surface-border px-2 py-1.5 text-sm">
                <option value="mediator">Mediator</option>
                <option value="admin">Admin</option>
                <option value="admin_mediator">Admin + mediator</option>
              </select>
            </label>
          </div>
          <button type="button" className="btn-primary" disabled={busy || !name.trim() || !email.trim()}
            onClick={() => { onCreate({ display_name: name.trim(), email: email.trim(), role }); setAdding(false); setName(''); setEmail(''); }}>
            Create account
          </button>
        </div>
      )}
      <ul className="divide-y divide-surface-border">
        {mediators.map((m) => (
          <li key={m.id} className="flex items-center gap-3 py-2.5 text-sm">
            <div>
              <div className={`font-medium ${m.is_active ? 'text-gray-900' : 'text-gray-400 line-through'}`}>{m.display_name}</div>
              <div className="text-xs text-gray-500">{m.email} · {m.role === 'admin_mediator' ? 'admin + mediator' : m.role}</div>
            </div>
            <button type="button" className="btn-secondary !py-1 !text-xs ml-auto" disabled={busy}
              onClick={() => onToggle(m.id, !m.is_active)}>
              {m.is_active ? 'Deactivate' : 'Reactivate'}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
